import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
} from '@/shared/shadcn/components/breadcrumb';
import { Separator } from '@/shared/shadcn/components/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/shared/shadcn/components/sidebar';
import { AppSidebar } from '@/components/admin/navigation/AppSidebar';
import { Toaster } from '@/shared/shadcn/components/toaster';
import React, { useEffect } from 'react';
import { ROUTES } from '@/constants/routes';
import { useRouter } from 'next/router';
import { useWebSocketEntityUpdatesContext } from '@/utils/websocket-entity-updates/webSocketEntityUpdatesProvider';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { getConstant } from '@/redux/constant/constant.thunks';
import processTextMessage from '@/components/Map/MapViewer/utils/websocket/processTextMessage';
import processProject from '@/components/admin/websocket/processProject';
import { MinervaVersionInfo } from '@/components/admin/MinervaVersionInfo';
import { getConfiguration } from '@/redux/configuration/configuration.thunks';
import {
  ENTITY_TYPES,
  TEXT_MESSAGE_OPERATION_TYPES,
} from '@/utils/websocket-entity-updates/webSocketEntityUpdates.constants';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps): JSX.Element => {
  const { lastJsonMessage } = useWebSocketEntityUpdatesContext();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getConstant());
    dispatch(getConfiguration());
  }, [dispatch]);

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }
    if (lastJsonMessage.type === TEXT_MESSAGE_OPERATION_TYPES.TEXT_MESSAGE) {
      processTextMessage(lastJsonMessage);
    } else if (
      'entityType' in lastJsonMessage &&
      lastJsonMessage.entityType === ENTITY_TYPES.PROJECT
    ) {
      processProject({ data: lastJsonMessage });
    }
  }, [lastJsonMessage]);

  const router = useRouter();
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() =>
                      router.push(ROUTES.ADMIN.PROJECTS.route, ROUTES.ADMIN.PROJECTS.url)
                    }
                    href="#"
                  >
                    MINERVA Admin
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <div className="ml-auto min-w-0 px-4">
            <MinervaVersionInfo />
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">{children}</div>
      </SidebarInset>
      <Toaster />
    </SidebarProvider>
  );
};

export default AdminLayout;
